import { ApiState, KAryTreeNode, ServerResponseN } from "../../../types/baseType";
import { AsObjectHashMap, foldTreeNodes } from "../../../context/map/utilMutableStore";
import {
    createAsyncThunk,
    createEntityAdapter,
    createSlice, EntityId,
    PayloadAction,
    prepareAutoBatched
} from "@reduxjs/toolkit";
import { applyExtraReducer } from "../weather";
import { WsChange } from "@api/socket/WebSocketService";
import { match, P } from "ts-pattern";
import { array, string } from "fp-ts";
import logger from "@utils/logger";
import { UnitHashElement } from "../../../context/map/units/unitsStore";
import { WsChangePayload } from "../geoZones";
import position from "./wsHandlers/position";
import track from "./wsHandlers/track";

const httpClient = require('@infobis/api-module').default;

type UnitNode = KAryTreeNode<UnitHashElement>


interface UnitState extends ApiState {
    apiError: undefined,
    isApiLoading: false,
    unitChangedIds: string[],
}

const fetch = createAsyncThunk(
    'unitTree',
    async (_, thunkAPI) => {
        try {
            const response = await httpClient.makeRequest({
                url: `/unitTree`,
                params: {
                    method: 'GET',
                },
                authorize: true
            }) as ServerResponseN<UnitNode>
            if (response) {
                return response
            } else {
                throw new Error('Invalid response ' + response)
            }
        } catch (e: any) {
            const message = `An error occurred while fetching API ` + (e?.message || e || '')
            return thunkAPI.rejectWithValue({ message })
        }
    }
)

const log = logger.getLogger("unit.store")
const unitAdapter = createEntityAdapter<UnitHashElement, EntityId>({ selectId: (e) => e.glid })
export const unitSelectors = unitAdapter.getSelectors()

const slice = createSlice({
    name: 'unitTreeSlice',
    initialState: unitAdapter.getInitialState<UnitState>({
        isApiLoading: false,
        apiError: undefined,
        unitChangedIds: []
    }),
    reducers: {
        commit(state: UnitState, action: PayloadAction<{ changes: string[] }>) {
            state.unitChangedIds = array.difference(string.Eq)(state.unitChangedIds, action.payload.changes)
        },
        applyWsChange: {
            prepare: prepareAutoBatched<WsChangePayload>(),
            reducer(state, actions: PayloadAction<WsChangePayload>) {
                log.debug("Apply changes, count - ", actions.payload.changes.length)
                state.unitChangedIds.push(...array.uniq(string.Eq)(array.reduce<WsChange, string[]>([],
                    (acc: string[], change: WsChange) => {
                        let unit = state.entities[change.id]
                        if (unit)
                            track(unit)(position(unit)(match(change.values)))
                                .with(P._, () => unit!.dynamic = { ...change.values })
                                .exhaustive()
                        return array.append(String(change.id))(acc)
                    })(actions.payload.changes)))
            }
        }
    },
    extraReducers: (builder) => {
        applyExtraReducer(builder, fetch, {
            fulfillReducer: (state, action) => {
                const data: {
                    entities: AsObjectHashMap<UnitHashElement>
                } = {
                    entities: {}
                }
                const {
                    entities
                } = foldTreeNodes<UnitHashElement, typeof data>(data)(action.payload,
                    (acc, node, parentNode) => {
                        node.groupName = (parentNode?.groupName || []).concat(node.glid)
                        node.dynamic = {}
                        acc.entities[node.glid] = node

                        return [acc, node]
                    })
                unitAdapter.removeAll(state)
                unitAdapter.setMany(state, entities)
            }
        })
    },
})

export const actions = { ...slice.actions, fetch }
export default slice.reducer